import {t} from '@lingui/core/macro';

import {PALETTE} from 'shared/config/model/palette';

import IApiViolation from '../../../shared/api/model/IApiViolation';
import {PortCheckStatus, PortDesiredState, PortMonitorStatus} from '../model/PortMonitor/IPortMonitor';

export interface IPortMonitorAddressSource {
    name?: string | null,
    ip: string,
    port: number,
}

const IPV4_OCTET = '(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)';
const IPV4_PATTERN = new RegExp(`^${IPV4_OCTET}(\\.${IPV4_OCTET}){3}$`);
// eslint-disable-next-line max-len
const IPV6_PATTERN = /^(([\da-f]{1,4}:){7}[\da-f]{1,4}|([\da-f]{1,4}:){1,7}:|([\da-f]{1,4}:){1,6}:[\da-f]{1,4}|([\da-f]{1,4}:){1,5}(:[\da-f]{1,4}){1,2}|([\da-f]{1,4}:){1,4}(:[\da-f]{1,4}){1,3}|([\da-f]{1,4}:){1,3}(:[\da-f]{1,4}){1,4}|([\da-f]{1,4}:){1,2}(:[\da-f]{1,4}){1,5}|[\da-f]{1,4}:(:[\da-f]{1,4}){1,6}|:((:[\da-f]{1,4}){1,7}|:))$/i;

const MIN_PORT = 1;
const MAX_PORT = 65535;

export function isValidPortMonitorIp(value: string): boolean {
    const ip = value.trim();

    return IPV4_PATTERN.test(ip) || IPV6_PATTERN.test(ip);
}

export function isValidPort(value: string | number): boolean {
    const port = `${value}`.trim();

    if (!/^[1-9]\d*$/.test(port)) {
        return false;
    }

    return Number(port) >= MIN_PORT && Number(port) <= MAX_PORT;
}

export function getPortMonitorAddress(ip: string, port: number): string {
    return ip.includes(':') ? `[${ip}]:${port}` : `${ip}:${port}`;
}

export function getPortMonitorName({name, ip, port}: IPortMonitorAddressSource): string {
    return name?.trim() || getPortMonitorAddress(ip, port);
}

export function getPortMonitorStateLabel(status: PortMonitorStatus, ok: boolean | null): string {
    if (status === 'paused') {
        return t`На паузе`;
    }

    if (ok === null) {
        return t`Нет проверок`;
    }

    return ok ? t`Исправен` : t`Инцидент`;
}

export function getPortDesiredStateLabel(desiredState: PortDesiredState): string {
    return desiredState === 'open' ? t`Открыт` : t`Закрыт`;
}

export function getPortCheckStatusLabel(status: PortCheckStatus): string {
    if (status === 'connected') {
        return t`Открыт`;
    }

    if (status === 'refused') {
        return t`Закрыт`;
    }

    return t`Не удалось проверить`;
}

export function isPortMonitorLimitViolation(violations?: IApiViolation[]): boolean {
    return (violations ?? []).some(({propertyPath}) => propertyPath === 'status');
}

export function getPortCheckDesiredLabel(isDesired: boolean | null): string {
    if (isDesired === null) {
        return '—';
    }

    return isDesired ? t`Да` : t`Нет`;
}

export const colorPortMonitorState = (status: PortMonitorStatus, ok: boolean | null): string => {
    if (status === 'paused') {
        return PALETTE.neutralMuted;
    }

    if (ok === null) {
        return PALETTE.neutral;
    }

    return ok ? PALETTE.success : PALETTE.error;
};

export type PortMonitorModalMode = 'batch' | 'create' | 'edit';

export function getPortMonitorModalMode(isBatchMode: boolean, isNew: boolean): PortMonitorModalMode {
    if (isBatchMode) {
        return 'batch';
    }

    return isNew ? 'create' : 'edit';
}

export type PortMonitorModalType = 'edit' | 'delete' | 'pause' | 'resume' | 'tags' | 'limit' | null;

export function getPortMonitorDateFormat(isEnglish: boolean): string {
    return isEnglish ? 'YYYY-MM-DD HH:mm' : 'DD.MM.YYYY HH:mm';
}

export type PortMonitorBackgroundKind ='en' | 'paused' | 'problem' | 'ok';

export function getPortMonitorBackgroundKind(
    monitor: {status: PortMonitorStatus, ok: boolean | null},
    isEnglish: boolean,
): PortMonitorBackgroundKind {
    if (isEnglish) {
        return 'en';
    }

    if (monitor.status === 'paused') {
        return 'paused';
    }

    return monitor.ok === false ? 'problem' : 'ok';
}
